import { AfterViewChecked, ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { Platform } from '@ionic/angular';
import { IonRouterOutlet, IonicModule, ModalController } from '@ionic/angular/lazy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AbstractComponentMobile, UiMobileModule } from '@web/ui-mobile';
import { filter, map, startWith, switchMap, take, timer } from 'rxjs';
import { register } from 'swiper/element/bundle';
import { environment } from '../environments/environment';
import { LoadingData } from './models/loading-data.model';
import { RatingModalComponent } from './pages/rating-modal/rating-modal.component';
import { ClientService } from './services/client.service';
import { LoadingService } from './services/loading.service';
import { SecurityService } from './services/security.service';
import { compareVersionName } from './utils/version-utils';

register();

@Component({
    selector: 'web-root',
    templateUrl: 'app.component.html',
    imports: [IonicModule, UiMobileModule, TranslateModule],
})
export class AppComponent extends AbstractComponentMobile implements OnInit, AfterViewChecked {
    platform = inject(Platform);
    translateService = inject(TranslateService);
    private router = inject(Router);
    private zone = inject(NgZone);
    private loadingService = inject(LoadingService);
    private cdRef = inject(ChangeDetectorRef);
    private clientService = inject(ClientService);
    private securityService = inject(SecurityService);
    private modalCtrl = inject(ModalController);
    private routerOutlet = inject(IonRouterOutlet, { optional: true });

    public override tag = '[AppComponent]';

    public loadingData?: LoadingData;
    protected tmpLoadingData?: LoadingData;

    protected currentAppVersion: string | undefined = undefined;
    protected availableAppVersion: string | undefined = undefined;

    public isUpdateAlertOpen = false;

    public alertButtons: { text: string; role: string; handler: () => void; cssClass: string }[] = [];

    constructor() {
        super();

        console.log(environment.env);

        this.subs['getTradAlertButton'] = this.translateService
            .get('COMPONENTS.UPDATE_ALERT.UPDATE')
            .subscribe(trad => {
                this.alertButtons = [
                    {
                        text: trad,
                        role: 'confirm',
                        handler: () => {
                            this.openAppStore();
                        },
                        cssClass: 'primary-button',
                    },
                ];
            });

        this.initializeApp();
    }

    ngOnInit(): void {
        this.subs['loadingData'] = this.loadingService.loadingData$.subscribe({
            next: (value?: LoadingData) => {
                this.tmpLoadingData = value;
            },
            error: (err: any) => console.error(err),
        });
    }

    // https://stackoverflow.com/questions/43513421/ngif-expression-has-changed-after-it-was-checked
    // https://medium.com/better-programming/expressionchangedafterithasbeencheckederror-in-angular-what-why-and-how-to-fix-it-c6bdc0b22787
    ngAfterViewChecked() {
        if (this.loadingData !== this.tmpLoadingData) {
            this.loadingData = this.tmpLoadingData;
            this.cdRef.detectChanges();
        }
    }

    private async configStatusBar() {
        if ((this.platform.is('android') || Capacitor.getPlatform() === 'android') && this.platform.is('capacitor')) {
            await StatusBar.setStyle({ style: Style.Dark });
            await StatusBar.setBackgroundColor({ color: '#101010' });
        }
    }

    private async initializeApp(): Promise<void> {
        await this.platform.ready();

        console.log('App Ready!');

        this.configStatusBar();

        this.checkAppUpdate();

        App.addListener('backButton', ({ canGoBack }) => {
            if (!canGoBack) {
                App.exitApp();
            }
        });

        App.addListener('resume', () => {
            this.checkAppUpdate();
        });

        // Deep link from mail or qrcode
        // https://capacitorjs.com/docs/guides/deep-links#angular
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.zone.run(() => {
                // Must match host where the .well-known files are uploaded
                // Currently on stream.sunology.eu
                const slug = event.url.split('.eu').pop();
                if (slug) {
                    this.router.navigateByUrl(slug);
                }
                // If no match, do nothing - let regular routing
                // logic take over
            });
        });

        // After 30s, wait until the user is logged in and on an authenticated page before prompting
        this.subs[this.tag + 'checkPromptRating'] = timer(30 * 1000)
            .pipe(
                switchMap(() =>
                    this.router.events.pipe(
                        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                        map(event => event.urlAfterRedirects),
                        startWith(this.router.url),
                    ),
                ),
                filter(url => this.canPromptRating(url)),
                take(1),
            )
            .subscribe(() => this.presentRatingModal());
    }

    private checkAppUpdate() {
        if (Capacitor.getPlatform() !== 'web') {
            Promise.all([this.getCurrentAppVersion(), this.getAvailableAppVersion()]).then(values => {
                this.currentAppVersion = values[0];
                this.availableAppVersion = values[1];
                if (values[0] && values[1] && compareVersionName(values[0], values[1]) === 1) {
                    this.showAlert(true);
                }
            });
        }
    }

    public async openAppStore(): Promise<void> {
        // @capawesome/capacitor-app-update 7.x removed the `country` fallback lookup on iOS and now
        // requires the numeric Apple App Store id (see https://apps.apple.com/fr/app/sunology-stream/id6463779208).
        await AppUpdate.openAppStore({ appId: '6463779208' });
    }

    private getCurrentAppVersion = async () => {
        const result = await AppUpdate.getAppUpdateInfo({ country: 'FR' });
        if (Capacitor.getPlatform() === 'android') {
            return result.currentVersionCode;
        } else {
            return result.currentVersionName;
        }
    };

    private getAvailableAppVersion = async () => {
        const result = await AppUpdate.getAppUpdateInfo({ country: 'FR' });
        if (Capacitor.getPlatform() === 'android') {
            return result.availableVersionCode;
        } else {
            return result.availableVersionName;
        }
    };

    public showAlert(open: boolean) {
        this.isUpdateAlertOpen = open;
    }

    private canPromptRating(url: string): boolean {
        const isPublicPage = url.startsWith('/login') || url.startsWith('/onboarding');
        return !isPublicPage && !!this.securityService.getUser() && this.clientService.shouldPromptRating;
    }

    async presentRatingModal(): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: RatingModalComponent,
            initialBreakpoint: 0.9,
            handle: false,
        });
        await modal.present();
    }
}
